import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { User, LogOut, LayoutDashboard, MessageSquare, Home } from "lucide-react";
import Logo from "./Logo";
import EmailVerificationBanner from "./EmailVerificationBanner";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogin = () => {
    navigate("/auth");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const dashboardLink = user?.role === "landlord" ? "/landlord-dashboard" : user?.role === "admin" ? "/admin" : "/dashboard";

  return (
    <>
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#faf8f3]/80 border-b border-black/[0.06]" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 md:px-12 flex items-center justify-between h-16">
        <Logo />

        <div className="flex items-center gap-4 sm:gap-6">
          <Link to="/browse" className="text-sm font-medium text-[#0d0d0d] hover:text-[#1a6b3c] transition-colors" data-testid="nav-browse">
            Browse
          </Link>
          <Link to="/contact" className="hidden sm:inline text-sm font-medium text-[#0d0d0d] hover:text-[#1a6b3c] transition-colors" data-testid="nav-contact">
            Contact
          </Link>
          {user && (
            <Link to="/messages" className="hidden sm:inline text-sm font-medium text-[#0d0d0d] hover:text-[#1a6b3c] transition-colors" data-testid="nav-messages">
              Messages
            </Link>
          )}
        </div>

        <div className="flex items-center gap-3">
          {!user ? (
            <Button
              data-testid="nav-login-btn"
              onClick={handleLogin}
              className="rounded-full bg-[#1a6b3c] hover:bg-[#15562f] text-white px-5 text-sm"
            >
              Sign In
            </Button>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-user-menu" className="flex items-center gap-2 rounded-full border border-black/[0.08] p-1.5 pr-4 hover:shadow-md transition-shadow bg-white">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#1a6b3c] flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <span className="text-sm font-medium text-[#0d0d0d] max-w-[80px] truncate hidden sm:inline">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white rounded-xl border border-black/[0.08]">
                <div className="px-3 py-2">
                  <p className="text-sm font-semibold text-[#0d0d0d]">{user.name}</p>
                  <p className="text-xs text-[#888] capitalize">{user.role}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="nav-dashboard-link" onClick={() => navigate(dashboardLink)} className="cursor-pointer">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-profile-link" onClick={() => navigate("/profile")} className="cursor-pointer">
                  <User className="w-4 h-4 mr-2" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-messages-link" onClick={() => navigate("/messages")} className="cursor-pointer">
                  <MessageSquare className="w-4 h-4 mr-2" /> Messages
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="nav-logout-btn" onClick={handleLogout} className="cursor-pointer text-red-600">
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </nav>
    <EmailVerificationBanner />
    </>
  );
}
